import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Search, Save, CheckCircle,
  Building2, Package, FileText, Calendar, Truck, AlertTriangle, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api, { vendorsAPI, productsAPI, warehousesAPI } from '@/services/api';
import HighlightText from '@/components/HighlightText';

const modeOptions = [
  { value: 'courier', label: 'Courier' },
  { value: 'hand_delivery', label: 'Hand Delivery' },
  { value: 'truck', label: 'Truck/Vehicle' },
  { value: 'other', label: 'Other' }
];
// Returns the selling price INCL. GST (same basis as MRP and the Product Master)
const calculateSPPriceStatic = (costPrice, mrp, pricingType, percent, gstPercent = 0) => {
  const cost = parseFloat(costPrice) || 0;
  const m = parseFloat(mrp) || 0;
  const g = parseFloat(gstPercent) || 0;
  if (pricingType === 'markup' && cost > 0) {
    const excl = cost * (1 + (percent / 100));
    return Math.round((g > 0 ? excl * (1 + g / 100) : excl) * 100) / 100;
  }
  if (pricingType === 'markdown' && m > 0) return Math.round(m * (1 - (percent / 100)) * 100) / 100;
  return null;
};

// Margin % of SP1 (excl. tax) over the landed unit cost
const marginPercentOf = (priceIncl, cost, gst) => {
  const p = parseFloat(priceIncl) || 0;
  const c = parseFloat(cost) || 0;
  const g = parseFloat(gst) || 0;
  if (p <= 0) return 0;
  const excl = g > 0 ? p / (1 + g / 100) : p;
  if (excl <= 0) return 0;
  return Math.round(((excl - c) / excl) * 1000) / 10;
};

// Selling price (incl. tax) that yields the given margin % over cost
const priceForMargin = (marginPct, cost, gst) => {
  const m = parseFloat(marginPct) || 0;
  const c = parseFloat(cost) || 0;
  const g = parseFloat(gst) || 0;
  if (c <= 0 || m >= 100) return 0;
  const excl = c / (1 - m / 100);
  return Math.round((g > 0 ? excl * (1 + g / 100) : excl) * 100) / 100;
};

const SP_LABELS = {
  1: 'SP1 (Retail)',
  2: 'SP2 (Wholesale)',
  3: 'SP3 (Dealer)',
  4: 'SP4 (Franchise)',
  5: 'SP5 (Special)'
};


const CreateGRNLocalPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: editGrnId } = useParams();
  const isEdit = Boolean(editGrnId);
  const poId = searchParams.get('po_id');
  const { toast } = useToast();

  // Form state
  const [vendor, setVendor] = useState(null);
  const [warehouse, setWarehouse] = useState(null);
  const [po, setPo] = useState(null);
  const [grnNumber, setGrnNumber] = useState('');
  const [grnStatus, setGrnStatus] = useState(null);
  const [linkedPoId, setLinkedPoId] = useState(null);
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);
  const [modeOfReceive, setModeOfReceive] = useState('courier');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [dcNumber, setDcNumber] = useState('');
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  
  // Footer adjustments
  const [billDiscountPercent, setBillDiscountPercent] = useState(0);
  const [billDiscountAmount, setBillDiscountAmount] = useState(0);
  const [otherChargesAdd, setOtherChargesAdd] = useState(0);
  const [otherChargesDeduct, setOtherChargesDeduct] = useState(0);
  const [roundOff, setRoundOff] = useState(0);

  // Lookup data
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const searchReqRef = useRef(0);
  const [securityCode, setSecurityCode] = useState('');
  const [marginBlock, setMarginBlock] = useState(null);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vendorsRes, warehousesRes] = await Promise.all([
          vendorsAPI.list(),
          warehousesAPI.list()
        ]);
        setVendors(vendorsRes.data || []);
        const whList = warehousesRes.data?.items || warehousesRes.data || [];
        setWarehouses(whList);
        // Single-warehouse deployments: pre-select it
        if (whList.length === 1) setWarehouse(prev => prev || whList[0]);
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
      }
    };
    fetchData();
  }, [toast]);

  // Load PO if provided
  useEffect(() => {
    if (poId && !isEdit) {
      const loadPO = async () => {
        setLoading(true);
        try {
          const res = await api.get(`/po-v2/${poId}`);
          const poData = res.data;
          setPo(poData);
          
          // Set vendor from snapshot
          if (poData.vendor_snapshot) {
            setVendor({
              id: poData.vendor_id,
              ...poData.vendor_snapshot
            });
          }
          
          // Set warehouse
          if (poData.warehouse_id) {
            const wh = warehouses.find(w => w.id === poData.warehouse_id);
            setWarehouse(wh || { id: poData.warehouse_id, name: poData.warehouse_name });
          }
          
          // Convert PO items to GRN items
          const grnItems = (poData.items || []).map(item => ({
            product_id: item.product_id,
            product_name: item.product_name,
            sku: item.sku,
            hsn_code: item.hsn_code || '',
            uom: item.uom || 'PCS',
            ordered_qty: item.qty,
            received_qty: item.qty,
            accepted_qty: item.qty,
            rejected_qty: 0,
            unit_price: item.unit_price_inr || item.unit_price,
            gst_percent: item.gst_percent || 0,
            discount_percent: item.discount_percent || 0,
            discount_amount: item.discount_amount || 0,
            gross_discount_amount: item.gross_discount_amount || 0,
            scheme_discount_percent: item.scheme_discount_percent || 0,
            scheme_discount_amount: item.scheme_discount_amount || 0,
            gross_scheme_discount_amount: item.gross_scheme_discount_amount || 0,
            // Batch fields - to be filled
            lot_batch_number: '',
            manufacturing_date: '',
            best_before_date: '',
            expiry_date: '',
            mrp: 0,
            sp_levels: []
          }));
          
          // Seed pricing levels / GST / MRP from Product Master
          try {
            const ids = grnItems.map(i => i.product_id);
            const res = await api.post('/products/pricing-levels', ids);
            const info = res.data || {};
            grnItems.forEach(gi => {
              const pi = info[gi.product_id];
              if (!pi) return;
              if (!gi.gst_percent) gi.gst_percent = pi.tax_rate || 0;
              if (!gi.mrp) gi.mrp = pi.mrp || 0;
              const levels = pi.pricing_levels || [];
              gi.sp_levels = levels.map(l => l.level);
              levels.forEach(l => {
                const n = l.level;
                const pType = l.pricing_type || 'markup';
                const pPercent = l.pricing_percent || 0;
                gi[`pricing_type_${n}`] = pType;
                gi[`pricing_percent_${n}`] = pPercent;
                gi[`selling_price_${n}`] = l.selling_price ?? null;
                const calc = (pType !== 'flat' && pPercent > 0)
                  ? calculateSPPriceStatic(gi.unit_price, gi.mrp, pType, pPercent, gi.gst_percent)
                  : null;
                gi[`new_sp${n}`] = calc ?? (l.selling_price ?? null);
              });
            });
          } catch (e) {
            console.log('Failed to load pricing levels', e);
          }
          
          setItems(grnItems);
        } catch (error) {
          toast({ title: 'Error', description: 'Failed to load PO', variant: 'destructive' });
        } finally {
          setLoading(false);
        }
      };
      loadPO();
    }
  }, [poId, warehouses, toast, isEdit]);

  // Load existing GRN for edit mode
  useEffect(() => {
    if (!isEdit) return;
    const loadGRN = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/grn-v2/local/${editGrnId}`);
        const g = res.data;
        if (!['draft', 'pending_verification'].includes(g.status)) {
          toast({
            title: 'Not editable',
            description: 'Only Draft or Pending Verification GRNs can be edited',
            variant: 'destructive'
          });
          navigate('/hq/purchase/grn');
          return;
        }
        setGrnNumber(g.grn_number || '');
        setGrnStatus(g.status);
        setLinkedPoId(g.po_id || null);
        if (g.po_number) setPo({ po_number: g.po_number });
        setVendor({ id: g.vendor_id, ...(g.vendor_snapshot || {}) });
        setWarehouse({ id: g.warehouse_id, name: g.warehouse_name });
        setReceivedDate(g.received_date || new Date().toISOString().split('T')[0]);
        setModeOfReceive(g.mode_of_receive || 'courier');
        setInvoiceNumber(g.invoice_number || '');
        setDcNumber(g.dc_number || '');
        setBillDiscountPercent(g.bill_discount_percent || 0);
        setBillDiscountAmount(g.bill_discount_percent ? 0 : (g.bill_discount_amount || 0));
        setOtherChargesAdd(g.other_charges_add || 0);
        setOtherChargesDeduct(g.other_charges_deduct || 0);
        setRoundOff(g.round_off || 0);
        setNotes(g.notes || '');

        const loadedItems = (g.items || []).map(it => {
          const item = {
            product_id: it.product_id,
            product_name: it.product_name,
            sku: it.sku,
            hsn_code: it.hsn_code || '',
            uom: it.uom || 'PCS',
            ordered_qty: it.ordered_qty || 0,
            received_qty: it.received_qty || 0,
            rejected_qty: it.rejected_qty || 0,
            free_qty: it.free_qty || 0,
            scanned_barcode: it.scanned_barcode || '',
            rejection_reason: it.rejection_reason || '',
            unit_price: it.unit_price || 0,
            gst_percent: it.gst_percent || 0,
            discount_percent: it.discount_percent || 0,
            discount_amount: it.discount_amount || 0,
            gross_discount_amount: it.gross_discount_amount || 0,
            scheme_discount_percent: it.scheme_discount_percent || 0,
            scheme_discount_amount: it.scheme_discount_amount || 0,
            gross_scheme_discount_amount: it.gross_scheme_discount_amount || 0,
            lot_batch_number: it.lot_batch_number || '',
            manufacturing_date: it.manufacturing_date || '',
            best_before_date: it.best_before_date || '',
            expiry_date: it.expiry_date || '',
            mrp: it.mrp || 0,
            sp_levels: []
          };
          for (let n = 1; n <= 5; n++) {
            const price = it[`selling_price_${n}`];
            const pType = it[`pricing_type_${n}`];
            if (price == null && !pType) continue;
            item.sp_levels.push(n);
            item[`pricing_type_${n}`] = pType || 'markup';
            item[`pricing_percent_${n}`] = it[`pricing_percent_${n}`] || 0;
            item[`selling_price_${n}`] = price ?? null;
            item[`new_sp${n}`] = price ?? null;
          }
          return item;
        });
        setItems(loadedItems);
      } catch (error) {
        toast({
          title: 'Error',
          description: error.response?.data?.detail || 'Failed to load GRN',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };
    loadGRN();
  }, [isEdit, editGrnId, navigate, toast]);

  // Search products (debounced + out-of-order responses ignored)
  const searchProducts = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setProducts([]);
      return;
    }
    const reqId = ++searchReqRef.current;
    try {
      const res = await productsAPI.search(query.trim());
      if (reqId !== searchReqRef.current) return;
      setProducts(res.data?.items || res.data || []);
    } catch (error) {
      console.error('Product search error:', error);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchProducts(productSearch), 300);
    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);

  // Calculate totals
  const calculateTotals = useCallback(() => {
    let subtotal = 0;
    let gstTotal = 0;
    
    items.forEach(item => {
      const lineSubtotal = (item.received_qty || 0) * (item.unit_price || 0);
      // Line discount = gross_discount + gross_scheme_discount (per-line totals from Option A fields).
      // Backwards-compat: fall back to legacy discount_percent/discount_amount if new fields are 0.
      const legacyLineDisc = lineSubtotal * ((item.discount_percent || 0) / 100) + (item.discount_amount || 0);
      const newLineDisc = (item.gross_discount_amount || 0) + (item.gross_scheme_discount_amount || 0);
      const lineDiscount = newLineDisc > 0 ? newLineDisc : legacyLineDisc;
      const taxable = Math.max(0, lineSubtotal - lineDiscount);
      const lineGst = taxable * ((item.gst_percent || 0) / 100);
      
      subtotal += taxable;
      gstTotal += lineGst;
    });
    
    const billDisc = subtotal * (billDiscountPercent / 100) + billDiscountAmount;
    // Safely parse roundOff - handle string values during typing
    const roundOffValue = typeof roundOff === 'string' ? (parseFloat(roundOff) || 0) : (roundOff || 0);
    const grandTotal = subtotal - billDisc + gstTotal + otherChargesAdd - otherChargesDeduct + roundOffValue;
    
    return {
      subtotal: subtotal.toFixed(2),
      gstTotal: gstTotal.toFixed(2),
      billDiscount: billDisc.toFixed(2),
      grandTotal: grandTotal.toFixed(2)
    };
  }, [items, billDiscountPercent, billDiscountAmount, otherChargesAdd, otherChargesDeduct, roundOff]);

  const totals = calculateTotals();

  // Add product to items
  const addProduct = async (product) => {
    // If the current search text matches one of the product's barcodes, remember it
    // so we can print it on the row as the physically scanned reference.
    const scanInput = (productSearch || '').trim();
    const allBarcodes = [product.barcode, ...(product.barcodes || []).map(b => b.barcode)].filter(Boolean);
    const scannedBarcode = scanInput && allBarcodes.includes(scanInput) ? scanInput : '';

    // Duplicate rule (barcode-aware):
    //  - If a barcode was scanned and the SAME (product, barcode) line exists → block
    //  - If scanning a DIFFERENT barcode of the same product → allow a new row
    //  - If adding by name search → block when any row of this product already exists
    const dup = items.find(i => {
      if (i.product_id !== product.id) return false;
      if (scannedBarcode) return (i.scanned_barcode || '') === scannedBarcode;
      return true;
    });
    if (dup) {
      toast({
        title: scannedBarcode ? 'Same barcode already scanned' : 'Already added',
        description: scannedBarcode
          ? `Barcode ${scannedBarcode} for ${product.name} is already in the GRN. Scan a different barcode or edit the existing row.`
          : `${product.name} is already entered in this GRN. Edit the existing row instead of adding it again.`,
        variant: 'destructive',
      });
      return;
    }

    // Read prices from product master - check current_price object first
    const currentPrice = product.current_price || {};
    
    // Pricing levels come from Product Master only (backend: /products/search -> pricing_levels)
    const masterLevels = product.pricing_levels || [];
    
    // Get default cost price from product master (check current_price first)
    let costPrice = currentPrice.cost_price || currentPrice.basic_cost || 
                    product.cost_price || product.last_purchase_price || 0;
    
    // Get MRP from product master
    let mrp = currentPrice.mrp || product.mrp || 0;
    
    // Get GST/Tax percent from product master - PRIORITY: tax_rate first, then others
    let gstPercent = currentPrice.tax_rate ?? currentPrice.gst_percent ?? currentPrice.tax_percent ?? 
                     product.tax_rate ?? product.gst_percent ?? product.tax_percent ?? 0;
    
    // Product Master margin (SP1) - used for the critical margin-drop alert
    const masterSp1Level = masterLevels.find(l => l.level === 1);
    const masterMarginPercent = masterSp1Level?.selling_price
      ? marginPercentOf(masterSp1Level.selling_price, costPrice, gstPercent)
      : null;
    
    // Fetch last purchase history from vendor if vendor is selected
    if (vendor) {
      try {
        const vendorId = vendor.id || vendor.vendor_id;
        const res = await api.get(`/products/${product.id}/vendor-purchase-history?vendor_id=${vendorId}`);
        if (res.data?.found) {
          // Use values from last purchase (override product master values)
          costPrice = res.data.last_rate || costPrice;
          mrp = res.data.mrp || mrp;
          gstPercent = res.data.gst_percent > 0 ? res.data.gst_percent : gstPercent;
          
          toast({
            title: 'Previous Purchase Found',
            description: `Last Rate: ₹${res.data.last_rate}, MRP: ₹${res.data.mrp}, GST: ${res.data.gst_percent}%`,
          });
        }
      } catch (err) {
        console.log('No previous purchase history found');
      }
    }
    
    const newItem = {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      hsn_code: product.hsn_code || '',
      scanned_barcode: scannedBarcode,
      uom: product.purchase_uom || product.base_uom || 'PCS',
      ordered_qty: 0,
      received_qty: 1,
      rejected_qty: 0,
      free_qty: 0,
      unit_price: costPrice,
      gst_percent: gstPercent,
      discount_percent: 0,
      discount_amount: 0,
      gross_discount_amount: 0,
      scheme_discount_percent: 0,
      scheme_discount_amount: 0,
      gross_scheme_discount_amount: 0,
      lot_batch_number: '',
      manufacturing_date: '',
      best_before_date: '',
      expiry_date: '',
      mrp: mrp,
      master_margin_percent: masterMarginPercent,
      // Only the pricing levels configured in Product Master are active for this item
      sp_levels: masterLevels.map(l => l.level),
    };
    
    // Seed each master-defined level with its own type / percent / price
    masterLevels.forEach(lvl => {
      const n = lvl.level;
      const pType = lvl.pricing_type || 'markup';
      const pPercent = lvl.pricing_percent || 0;
      newItem[`pricing_type_${n}`] = pType;
      newItem[`pricing_percent_${n}`] = pPercent;
      newItem[`selling_price_${n}`] = lvl.selling_price ?? null;
      const calculated = (pType !== 'flat' && pPercent > 0 && (mrp > 0 || costPrice > 0))
        ? calculateSPPrice(costPrice, mrp, pType, pPercent, gstPercent)
        : null;
      newItem[`new_sp${n}`] = calculated ?? (lvl.selling_price ?? null);
    });
    
    if (masterLevels.length === 0) {
      toast({
        title: 'No pricing levels configured',
        description: `${product.name}: set selling price levels in Product Master (Product Pricing) first.`,
        variant: 'destructive'
      });
    }
    
    setItems([...items, newItem]);
    setProductDialogOpen(false);
    setProductSearch('');
  };
  
  // Calculate single SP price based on pricing type and percent
  const calculateSPPrice = (costPrice, mrp, pricingType, percent, gstPercent = 0) =>
    calculateSPPriceStatic(costPrice, mrp, pricingType, percent, gstPercent);

  // Update pricing for a specific SP level
  const updateSPPricing = (index, spLevel, pricingType, percent) => {
    const newItems = [...items];
    const item = newItems[index];
    const costPrice = parseFloat(item.unit_price) || 0;
    const mrp = parseFloat(item.mrp) || 0;
    
    // Calculate the new SP value based on pricing type
    let newPrice = null;
    if (pricingType === 'markup' || pricingType === 'markdown') {
      newPrice = percent > 0
        ? calculateSPPriceStatic(costPrice, mrp, pricingType, percent, item.gst_percent)
        : item[`new_sp${spLevel}`];
    } else if (pricingType === 'flat') {
      // Keep existing value for flat
      newPrice = item[`new_sp${spLevel}`] || item[`selling_price_${spLevel}`];
    }
    
    newItems[index] = {
      ...item,
      [`pricing_type_${spLevel}`]: pricingType,
      [`pricing_percent_${spLevel}`]: percent,
      [`new_sp${spLevel}`]: newPrice
    };
    
    setItems(newItems);
  };

  // ---- Discount recomputation helpers (Option A: per unit + line total) ----
  // Given the changed field on a line, recompute all six discount fields consistently.
  // Formula chain:
  //   disc_amount   = unit_price * disc_percent / 100                    (per unit)
  //   gross_disc    = qty * disc_amount                                  (line total)
  //   post_disc_unit = unit_price - disc_amount
  //   sch_disc_amt  = post_disc_unit * sch_disc_percent / 100            (per unit)
  //   gross_sch     = qty * sch_disc_amt                                 (line total)
  const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
  
  const recomputeDiscountFields = (item, changed) => {
    const qty = parseFloat(item.accepted_qty ?? item.received_qty) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    
    // ---- Regular discount pair ----
    let discPct = parseFloat(item.discount_percent) || 0;
    let discAmt = parseFloat(item.discount_amount) || 0;
    let grossDisc = parseFloat(item.gross_discount_amount) || 0;
    
    if (changed === 'discount_percent') {
      discAmt = unitPrice > 0 ? round2(unitPrice * discPct / 100) : 0;
      grossDisc = round2(qty * discAmt);
    } else if (changed === 'discount_amount') {
      discPct = unitPrice > 0 ? round2(discAmt / unitPrice * 100) : 0;
      grossDisc = round2(qty * discAmt);
    } else if (changed === 'gross_discount_amount') {
      discAmt = qty > 0 ? round2(grossDisc / qty) : 0;
      discPct = unitPrice > 0 ? round2(discAmt / unitPrice * 100) : 0;
    } else {
      // qty / unit_price changed - keep % as the driver, recompute amounts
      discAmt = unitPrice > 0 ? round2(unitPrice * discPct / 100) : 0;
      grossDisc = round2(qty * discAmt);
    }
    
    const postDiscUnit = Math.max(0, unitPrice - discAmt);
    
    // ---- Scheme discount pair ----
    let schPct = parseFloat(item.scheme_discount_percent) || 0;
    let schAmt = parseFloat(item.scheme_discount_amount) || 0;
    let grossSch = parseFloat(item.gross_scheme_discount_amount) || 0;
    
    if (changed === 'scheme_discount_percent') {
      schAmt = postDiscUnit > 0 ? round2(postDiscUnit * schPct / 100) : 0;
      grossSch = round2(qty * schAmt);
    } else if (changed === 'scheme_discount_amount') {
      schPct = postDiscUnit > 0 ? round2(schAmt / postDiscUnit * 100) : 0;
      grossSch = round2(qty * schAmt);
    } else if (changed === 'gross_scheme_discount_amount') {
      schAmt = qty > 0 ? round2(grossSch / qty) : 0;
      schPct = postDiscUnit > 0 ? round2(schAmt / postDiscUnit * 100) : 0;
    } else {
      // qty / unit_price / disc pair changed - keep sch % as the driver
      schAmt = postDiscUnit > 0 ? round2(postDiscUnit * schPct / 100) : 0;
      grossSch = round2(qty * schAmt);
    }
    
    return {
      discount_percent: discPct,
      discount_amount: discAmt,
      gross_discount_amount: grossDisc,
      scheme_discount_percent: schPct,
      scheme_discount_amount: schAmt,
      gross_scheme_discount_amount: grossSch,
    };
  };
  
  // Change a discount field on an item and cascade-update the pair
  const updateDiscountField = (index, field, value) => {
    const newItems = [...items];
    const v = value === '' ? 0 : parseFloat(value);
    newItems[index] = { ...newItems[index], [field]: isNaN(v) ? 0 : v };
    const recomputed = recomputeDiscountFields(newItems[index], field);
    newItems[index] = { ...newItems[index], ...recomputed };
    setItems(newItems);
  };

  // Also recalculate prices when unit_price or mrp changes - now per-SP
  const updateItemAndRecalcPricing = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    const item = newItems[index];
    const costPrice = parseFloat(field === 'unit_price' ? value : item.unit_price) || 0;
    const mrp = parseFloat(field === 'mrp' ? value : item.mrp) || 0;
    
    // Margin is derived from the current SP1 unless the user typed it
    if (field === 'unit_price' || field === 'mrp' || field === 'gst_percent') {
      newItems[index].margin_percent = null;
    }

    // If unit_price, mrp or GST changed, recalculate each active SP level by its own rule
    if (field === 'unit_price' || field === 'mrp' || field === 'gst_percent') {
      (item.sp_levels || []).forEach(n => {
        const pType = item[`pricing_type_${n}`];
        const pPercent = item[`pricing_percent_${n}`] || 0;
        if (pType && pType !== 'flat' && pPercent > 0) {
          newItems[index][`new_sp${n}`] = calculateSPPrice(costPrice, mrp, pType, pPercent, item.gst_percent);
        }
      });
    }
    
    // If unit_price or qty changed, refresh discount amounts (keep % as driver)
    if (field === 'unit_price' || field === 'accepted_qty' || field === 'received_qty') {
      const recomputed = recomputeDiscountFields(newItems[index], field);
      newItems[index] = { ...newItems[index], ...recomputed };
    }
    
    setItems(newItems);
  };

  // Update item
  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Margin is derived from the live SP1 unless the user typed a margin
    if (field === 'new_sp1' || field === 'gst_percent') {
      newItems[index].margin_percent = null;
    }
    
    // Received / accepted qty change - refresh gross discount amounts
    if (field === 'received_qty' || field === 'accepted_qty') {
      const recomputed = recomputeDiscountFields(newItems[index], field);
      newItems[index] = { ...newItems[index], ...recomputed };
    }
    
    setItems(newItems);
  };

  // Remove item
  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // Validate items
  const validateItems = () => {
    const today = new Date().toISOString().split('T')[0];
    
    // First pass: check for expired items with clear alert
    const expiredItems = items.filter((item, idx) => item.expiry_date && item.expiry_date < today);
    if (expiredItems.length > 0) {
      const itemNames = expiredItems.map((item, idx) => {
        const originalIdx = items.indexOf(item) + 1;
        return `Row ${originalIdx}: ${item.product_name} (Expiry: ${item.expiry_date})`;
      }).join('\n');
      
      toast({ 
        title: '🚫 Cannot Complete GRN - Expired Products Found!', 
        description: `Please update expiry dates for:\n${itemNames}`, 
        variant: 'destructive',
        duration: 10000
      });
      return false;
    }
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.lot_batch_number) {
        toast({ title: 'Validation Error', description: `Row ${i + 1}: Batch number is required`, variant: 'destructive' });
        return false;
      }
      if (!item.expiry_date) {
        toast({ title: 'Validation Error', description: `Row ${i + 1}: Expiry date is required`, variant: 'destructive' });
        return false;
      }
      if (!item.mrp || item.mrp <= 0) {
        toast({ title: 'Validation Error', description: `Row ${i + 1}: MRP is required`, variant: 'destructive' });
        return false;
      }
    }
    return true;
  };

  // Margin % on the line (SP1 vs unit cost) - editing it re-prices SP1
  const updateMarginPercent = (index, marginPct) => {
    if (marginPct >= 100) {
      toast({
        title: 'Invalid margin',
        description: 'Margin % must be below 100',
        variant: 'destructive'
      });
      return;
    }
    const newItems = [...items];
    const item = newItems[index];
    const cost = parseFloat(item.unit_price) || 0;
    const gst = parseFloat(item.gst_percent) || 0;
    const mrp = parseFloat(item.mrp) || 0;
    const newPrice = priceForMargin(marginPct, cost, gst);

    item.margin_percent = marginPct;
    if (newPrice > 0) {
      item.new_sp1 = newPrice;
      // keep the Level 1 rule in sync so the Product Master stays consistent
      if (item.pricing_type_1 === 'markdown' && mrp > 0) {
        item.pricing_percent_1 = Math.round(((mrp - newPrice) / mrp) * 1000) / 10;
      } else if (item.pricing_type_1 === 'markup' && cost > 0) {
        const excl = gst > 0 ? newPrice / (1 + gst / 100) : newPrice;
        item.pricing_percent_1 = Math.round(((excl - cost) / cost) * 1000) / 10;
      }
    }
    setItems(newItems);
  };

  // Build per-level pricing payload for only the levels defined in Product Master
  const buildLevelPayload = (item) => {
    const payload = {};
    for (let n = 1; n <= 5; n++) {
      const active = (item.sp_levels || []).includes(n);
      payload[`selling_price_${n}`] = active
        ? (item[`new_sp${n}`] != null ? parseFloat(item[`new_sp${n}`]) : (item[`selling_price_${n}`] ?? null))
        : null;
      payload[`pricing_type_${n}`] = active ? (item[`pricing_type_${n}`] || 'markup') : null;
      payload[`pricing_percent_${n}`] = active ? (parseFloat(item[`pricing_percent_${n}`]) || 0) : 0;
    }
    return payload;
  };

  // Save GRN
  const saveGRN = async (complete = false, codeOverride = null) => {
    if (!vendor) {
      toast({ title: 'Error', description: 'Please select a vendor', variant: 'destructive' });
      return;
    }
    if (!warehouse) {
      toast({ title: 'Error', description: 'Please select a warehouse', variant: 'destructive' });
      return;
    }
    if (items.length === 0) {
      toast({ title: 'Error', description: 'Please add at least one item', variant: 'destructive' });
      return;
    }
    if (!validateItems()) {
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        vendor_id: vendor.id || vendor.vendor_id,
        warehouse_id: warehouse.id,
        po_id: poId || linkedPoId || null,
        received_date: receivedDate,
        mode_of_receive: modeOfReceive,
        invoice_number: invoiceNumber || null,
        dc_number: dcNumber || null,
        items: items.map(item => ({
          product_id: item.product_id,
          received_qty: parseInt(item.received_qty) || 0,
          accepted_qty: parseInt(item.received_qty) || 0,  // Use received_qty as accepted
          rejected_qty: parseInt(item.rejected_qty) || 0,
          free_qty: parseInt(item.free_qty) || 0,
          scanned_barcode: item.scanned_barcode || null,
          rejection_reason: item.rejection_reason || null,
          unit_price: parseFloat(item.unit_price) || 0,
          hsn_code: item.hsn_code,
          gst_percent: parseFloat(item.gst_percent) || 0,
          discount_percent: parseFloat(item.discount_percent) || 0,
          discount_amount: parseFloat(item.discount_amount) || 0,
          gross_discount_amount: parseFloat(item.gross_discount_amount) || 0,
          scheme_discount_percent: parseFloat(item.scheme_discount_percent) || 0,
          scheme_discount_amount: parseFloat(item.scheme_discount_amount) || 0,
          gross_scheme_discount_amount: parseFloat(item.gross_scheme_discount_amount) || 0,
          lot_batch_number: item.lot_batch_number,
          manufacturing_date: item.manufacturing_date || null,
          best_before_date: item.best_before_date || null,
          expiry_date: item.expiry_date,
          mrp: parseFloat(item.mrp) || 0,
          // Only master-defined pricing levels are sent; the rest stay null
          ...buildLevelPayload(item)
        })),
        bill_discount_percent: parseFloat(billDiscountPercent) || 0,
        bill_discount_amount: parseFloat(billDiscountAmount) || 0,
        other_charges_add: parseFloat(otherChargesAdd) || 0,
        other_charges_deduct: parseFloat(otherChargesDeduct) || 0,
        round_off: parseFloat(roundOff) || 0,
        notes,
        security_code: codeOverride || securityCode || null
      };
      
      let grnId = editGrnId;
      if (isEdit) {
        await api.put(`/grn-v2/local/${editGrnId}/draft`, payload);
      } else {
        const res = await api.post('/grn-v2/local', payload);
        grnId = res.data?.id;
      }
      
      if (complete && grnId && grnStatus !== 'pending_verification') {
        await api.post(`/grn-v2/local/${grnId}/submit-for-verification`);
        toast({ title: 'Success', description: 'GRN submitted for warehouse verification. Stock will update after verification.' });
      } else if (isEdit) {
        toast({ title: 'Success', description: `GRN ${grnNumber} updated` });
      } else {
        toast({ title: 'Success', description: 'GRN created as draft' });
      }
      
      setMarginBlock(null);
      setSecurityCode('');
      navigate('/hq/purchase/grn');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === 'margin_reduction_blocked') {
        setMarginBlock({ violations: detail.violations || [], complete });
        setSaving(false);
        return;
      }
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save GRN', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  // Round-off handler - allow free typing of numbers, +, -, and .
  const handleRoundOffChange = (e) => {
    const v = e.target.value;
    // Allow empty, partial inputs like '-', '.', '-.', '+', '+.' while typing
    // Only allow: digits, one decimal point, optional leading +/-
    const isValidPartial = /^[+-]?\.?\d*\.?\d*$/.test(v);
    if (v === '' || isValidPartial) {
      setRoundOff(v);
    }
  };

  // Check expiry date for badge color - also warn if past date selected
  const getExpiryBadge = (expiryDate) => {
    if (!expiryDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    const expiry = new Date(expiryDate);
    const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return <Badge className="bg-red-500 text-white text-xs mt-1 animate-pulse">EXPIRED</Badge>;
    } else if (diffDays <= 90) {
      return <Badge className="bg-red-100 text-red-700 text-xs mt-1">&lt;3 months</Badge>;
    } else if (diffDays <= 180) {
      return <Badge className="bg-amber-100 text-amber-700 text-xs mt-1">&lt;6 months</Badge>;
    }
    return null;
  };

  // Expanded row state for spreadsheet-style items table
  const [expandedRows, setExpandedRows] = useState(new Set());
  const toggleExpand = (idx) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const expandAll = () => setExpandedRows(new Set(items.map((_, i) => i)));
  const collapseAll = () => setExpandedRows(new Set());
  const isRowComplete = (item) => {
    if (!item.received_qty || item.received_qty <= 0) return false;
    if (!item.unit_price || item.unit_price <= 0) return false;
    if (!item.lot_batch_number) return false;
    if (!item.expiry_date) return false;
    if (!item.mrp || item.mrp <= 0) return false;
    return true;
  };

  // Handle Enter key navigation - move to next field in same row
  const handleKeyDown = (e, currentIndex, currentField) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Define field order for Enter key navigation within a row (removed 'accepted')
      const fieldOrder = [
        'received', 'rejected', 'price', 'gst', 
        'batch', 'mfg', 'bestbefore', 'expiry', 'mrp',
        'pricing-percent', 'sp1', 'sp2', 'sp3', 'sp4', 'sp5'
      ];
      
      const currentFieldIndex = fieldOrder.indexOf(currentField);
      
      if (currentFieldIndex !== -1 && currentFieldIndex < fieldOrder.length - 1) {
        // Move to next field in same row
        const nextField = fieldOrder[currentFieldIndex + 1];
        const nextInput = document.querySelector(`[data-testid="grn-line-${nextField}-${currentIndex}"]`);
        if (nextInput) {
          nextInput.focus();
          if (nextInput.select) nextInput.select();
        }
      } else {
        // Last field in row - move to first field of next row if exists
        const nextRowFirstInput = document.querySelector(`[data-testid="grn-line-received-${currentIndex + 1}"]`);
        if (nextRowFirstInput) {
          nextRowFirstInput.focus();
          if (nextRowFirstInput.select) nextRowFirstInput.select();
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="pb-24 bg-gray-50 min-h-screen" data-testid="create-grn-local-page">
      <Toaster />

      {/* Sticky Header - always visible while scrolling */}
      <div className="sticky top-0 z-30 bg-white border-b shadow-sm px-4 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/hq/purchase/grn')} data-testid="grn-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-800 truncate">
                {isEdit ? `Edit GRN (Local) — ${grnNumber}` : 'Create GRN (Local)'}
              </h1>
              <p className="text-xs text-gray-500 truncate">
                {vendor ? (vendor.vendor_name || vendor.name) : 'Select vendor'} · {warehouse ? warehouse.name : 'Select warehouse'} · {items.length} item(s)
                {po ? ` · PO ${po.po_number}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-gray-500">Grand Total</p>
              <p className="text-xl font-bold text-teal-600" data-testid="sticky-grand-total">₹{Math.round(Number(totals.grandTotal || 0)).toLocaleString('en-IN')}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => saveGRN(false)} disabled={saving} data-testid="grn-save-draft-btn">
                <Save className="w-4 h-4 mr-1" />
                {saving ? 'Saving...' : (isEdit ? 'Update' : 'Save Draft')}
              </Button>
              {!(isEdit && grnStatus === 'pending_verification') && (
                <Button size="sm" onClick={() => saveGRN(true)} disabled={saving} className="bg-teal-500 hover:bg-teal-600" data-testid="grn-complete-btn">
                  <CheckCircle className="w-4 h-4 mr-1" /> {isEdit ? 'Update & Send' : 'Save & Send'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 lg:p-8">
      <div className="space-y-6">
        {/* Main Content (full-width) */}
        <div className="space-y-6">
          {/* Vendor & Warehouse */}
          <Card className="bg-white">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-500" />
                Vendor & Warehouse
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Vendor Selector */}
                <div>
                  <Label>Vendor *</Label>
                  <div 
                    className={`mt-1 p-3 border rounded-lg ${po ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer hover:border-teal-500'}`}
                    onClick={() => !po && setVendorDialogOpen(true)}
                    data-testid="grn-vendor-selector"
                  >
                    {vendor ? (
                      <div>
                        <p className="font-medium">{vendor.vendor_name || vendor.name}</p>
                        <p className="text-sm text-gray-500">{vendor.vendor_code || vendor.code}</p>
                      </div>
                    ) : (
                      <p className="text-gray-400">Click to select vendor</p>
                    )}
                  </div>
                </div>

                {/* Warehouse Selector */}
                <div>
                  <Label>Destination Warehouse *</Label>
                  <Select 
                    value={warehouse?.id || ''} 
                    onValueChange={(val) => setWarehouse(warehouses.find(w => w.id === val))}
                    disabled={!!po}
                  >
                    <SelectTrigger className="mt-1" data-testid="grn-warehouse-select">
                      <SelectValue placeholder="Select warehouse" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {warehouses.map(wh => (
                        <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Receipt Details */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <div>
                  <Label>Received Date *</Label>
                  <Input
                    type="date"
                    value={receivedDate}
                    onChange={(e) => setReceivedDate(e.target.value)}
                    className="mt-1"
                    data-testid="grn-received-date"
                  />
                </div>
                <div>
                  <Label>Mode of Receive</Label>
                  <Select value={modeOfReceive} onValueChange={setModeOfReceive}>
                    <SelectTrigger className="mt-1" data-testid="grn-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {modeOptions.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Invoice Number</Label>
                  <Input
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    className="mt-1"
                    placeholder="Vendor invoice #"
                    data-testid="grn-invoice-number"
                  />
                </div>
                <div>
                  <Label>DC Number</Label>
                  <Input
                    value={dcNumber}
                    onChange={(e) => setDcNumber(e.target.value)}
                    className="mt-1"
                    placeholder="Delivery challan #"
                    data-testid="grn-dc-number"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card className="bg-white">
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-teal-500" />
                Line Items ({items.length})
              </CardTitle>
              <div className="flex gap-2">
                {items.length > 0 && (
                  <>
                    <Button size="sm" variant="outline" onClick={expandAll} data-testid="grn-expand-all">
                      Expand All
                    </Button>
                    <Button size="sm" variant="outline" onClick={collapseAll} data-testid="grn-collapse-all">
                      Collapse All
                    </Button>
                  </>
                )}
                {!po && (
                  <Button 
                    onClick={() => setProductDialogOpen(true)}
                    size="sm"
                    className="bg-teal-500 hover:bg-teal-600"
                    data-testid="grn-line-add-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No items added yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Compact spreadsheet header */}
                  <div className="hidden md:grid grid-cols-[40px_minmax(180px,2fr)_80px_100px_80px_80px_80px_120px_60px_60px] gap-2 px-2 py-2 bg-gray-50 rounded text-[11px] uppercase tracking-wide text-gray-600 font-semibold border-b">
                    <div>#</div>
                    <div>Product / SKU</div>
                    <div className="text-right">Recv</div>
                    <div className="text-right">Unit ₹</div>
                    <div className="text-right">GST%</div>
                    <div className="text-right">Disc%</div>
                    <div className="text-right">Sch%</div>
                    <div className="text-right">Line Total</div>
                    <div className="text-center">Status</div>
                    <div className="text-center">Action</div>
                  </div>
                  {items.map((item, index) => {
                    const lineSubtotal = (item.received_qty || 0) * (item.unit_price || 0);
                    const grossDiscTotal = (item.gross_discount_amount || 0) + (item.gross_scheme_discount_amount || 0);
                    const legacyDisc = lineSubtotal * ((item.discount_percent || 0) / 100);
                    const lineDiscount = grossDiscTotal > 0 ? grossDiscTotal : legacyDisc;
                    const taxable = Math.max(0, lineSubtotal - lineDiscount);
                    const lineGst = taxable * ((item.gst_percent || 0) / 100);
                    const lineTotal = taxable + lineGst;
                    const isExpanded = expandedRows.has(index);
                    const complete = isRowComplete(item);
                    
                    return (
                    <div key={index} className="border rounded-lg overflow-hidden" data-testid={`grn-line-row-${index}`}>
                      {/* Compact row - always visible */}
                      <div
                        className={`grid grid-cols-[40px_minmax(180px,2fr)_80px_100px_80px_80px_80px_120px_60px_60px] gap-2 items-center px-2 py-2 ${isExpanded ? 'bg-teal-50 border-b' : 'hover:bg-gray-50'} cursor-pointer text-sm`}
                        onClick={() => toggleExpand(index)}
                        data-testid={`grn-line-compact-${index}`}
                      >
                        <div className="text-gray-500">{index + 1}</div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-800 truncate">{item.product_name}</p>
                          <p className="text-xs text-gray-500 truncate">{item.sku}{item.hsn_code ? ` · HSN ${item.hsn_code}` : ''}</p>
                          {item.scanned_barcode && (
                            <p className="text-xs text-indigo-600 font-mono truncate" title="Scanned barcode">📷 {item.scanned_barcode}</p>
                          )}
                        </div>
                        <div className="text-right font-mono">{item.received_qty || 0}</div>
                        <div className="text-right font-mono">₹{Number(item.unit_price || 0).toFixed(2)}</div>
                        <div className="text-right">{Number(item.gst_percent || 0).toFixed(2)}%</div>
                        <div className="text-right">{Number(item.discount_percent || 0).toFixed(2)}%</div>
                        <div className="text-right">{Number(item.scheme_discount_percent || 0).toFixed(2)}%</div>
                        <div className="text-right font-mono font-semibold text-teal-700">₹{lineTotal.toFixed(2)}</div>
                        <div className="text-center">
                          <span
                            className={`inline-block w-2 h-2 rounded-full ${complete ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            title={complete ? 'Row complete' : 'Missing: batch / expiry / MRP / qty / price'}
                          />
                        </div>
                        <div className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); toggleExpand(index); }}
                            data-testid={`grn-line-expand-${index}`}
                          >
                            <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </Button>
                        </div>
                      </div>
                      {/* Expanded detailed edit view */}
                      {isExpanded && (
                      <div className="p-4 bg-white" data-testid={`grn-line-expanded-${index}`}>
                        {/* Product Info Row */}
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-medium">{item.product_name}</p>
                            <p className="text-sm text-gray-500">{item.sku} | HSN: {item.hsn_code || '-'}</p>
                            {item.scanned_barcode && (
                              <p className="text-xs text-indigo-600 font-mono" title="Scanned barcode">📷 Scanned: {item.scanned_barcode}</p>
                            )}
                          </div>
                          {!po && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => removeItem(index)}
                              className="text-red-500"
                              data-testid={`grn-line-remove-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        {/* Quantity & Price Row */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-3">
                          {po && (
                            <div>
                              <Label className="text-xs text-gray-500">Ordered</Label>
                              <Input value={item.ordered_qty} disabled className="bg-gray-50" />
                            </div>
                          )}
                          <div>
                            <Label className="text-xs text-gray-500">Received *</Label>
                            <Input
                              type="number"
                              min="0"
                              value={item.received_qty}
                              onChange={(e) => updateItem(index, 'received_qty', parseInt(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, index, 'received')}
                              data-testid={`grn-line-received-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Rejected</Label>
                            <Input
                              type="number"
                              min="0"
                              value={item.rejected_qty || 0}
                              onChange={(e) => updateItem(index, 'rejected_qty', parseInt(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, index, 'rejected')}
                              data-testid={`grn-line-rejected-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-emerald-700 flex items-center gap-1" title="Bonus units from vendor — add to stock but not to pricing">
                              Free Qty
                            </Label>
                            <Input
                              type="number"
                              min="0"
                              value={item.free_qty || 0}
                              onChange={(e) => updateItem(index, 'free_qty', parseInt(e.target.value) || 0)}
                              className="border-emerald-200 focus:border-emerald-400"
                              data-testid={`grn-line-free-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Unit Price</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => updateItemAndRecalcPricing(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, index, 'price')}
                              data-testid={`grn-line-price-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">GST %</Label>
                            <Input
                              type="number"
                              value={item.gst_percent}
                              onChange={(e) => updateItemAndRecalcPricing(index, 'gst_percent', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, index, 'gst')}
                              data-testid={`grn-line-gst-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-emerald-700">Margin % (SP1)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={item.margin_percent ?? marginPercentOf(item.new_sp1, item.unit_price, item.gst_percent)}
                              onChange={(e) => updateMarginPercent(index, parseFloat(e.target.value) || 0)}
                              className="bg-emerald-50 border-emerald-200"
                              data-testid={`grn-line-margin-${index}`}
                            />
                            <p className="text-xs text-gray-400 mt-0.5" data-testid={`grn-line-margin-hint-${index}`}>
                              Master SP1: ₹{item.selling_price_1 ?? '-'} · margin now{' '}
                              {marginPercentOf(item.new_sp1, item.unit_price, item.gst_percent)}%
                            </p>
                            {(parseFloat(item.mrp) || 0) > 0 && (parseFloat(item.new_sp1) || 0) > (parseFloat(item.mrp) || 0) && (
                              <p className="text-xs text-red-600 mt-0.5" data-testid={`grn-line-margin-warning-${index}`}>
                                SP1 is above MRP
                              </p>
                            )}
                            {item.master_margin_percent != null &&
                              marginPercentOf(item.new_sp1, item.unit_price, item.gst_percent) < item.master_margin_percent - 0.05 && (
                              <div className="mt-1 rounded-md bg-red-600 text-white px-2 py-1.5" data-testid={`grn-line-margin-critical-${index}`}>
                                <p className="text-xs font-semibold flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" /> Margin dropped
                                </p>
                                <p className="text-[11px] text-red-50">
                                  {item.master_margin_percent}% → {marginPercentOf(item.new_sp1, item.unit_price, item.gst_percent)}% ·
                                  Management attention + security code required
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Discount Row (Option A - per unit + line total).
                            All 6 fields auto-fill each other via updateDiscountField. */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-3 bg-blue-50 p-3 rounded-lg">
                          <div>
                            <Label
                              className="text-xs text-blue-700"
                              title="Discount % (per line - the standard discount percentage from the vendor)"
                            >
                              Disc %
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.discount_percent ?? 0}
                              onChange={(e) => updateDiscountField(index, 'discount_percent', e.target.value)}
                              className="h-9"
                              data-testid={`grn-line-disc-pct-${index}`}
                            />
                          </div>
                          <div>
                            <Label
                              className="text-xs text-blue-700"
                              title="Discount Amount - per unit discount value (= Unit Price x Disc %)"
                            >
                              Disc Amt
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.discount_amount ?? 0}
                              onChange={(e) => updateDiscountField(index, 'discount_amount', e.target.value)}
                              className="h-9"
                              data-testid={`grn-line-disc-amt-${index}`}
                            />
                          </div>
                          <div>
                            <Label
                              className="text-xs text-blue-700"
                              title="Gross Discount Amount - line total discount (= Qty x Disc Amt)"
                            >
                              Gross Disc Amt
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.gross_discount_amount ?? 0}
                              onChange={(e) => updateDiscountField(index, 'gross_discount_amount', e.target.value)}
                              className="h-9 bg-white font-medium"
                              data-testid={`grn-line-gross-disc-${index}`}
                            />
                          </div>
                          <div>
                            <Label
                              className="text-xs text-indigo-700"
                              title="Scheme Discount % - applied on the post-discount unit price"
                            >
                              Sch Disc %
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.scheme_discount_percent ?? 0}
                              onChange={(e) => updateDiscountField(index, 'scheme_discount_percent', e.target.value)}
                              className="h-9"
                              data-testid={`grn-line-sch-pct-${index}`}
                            />
                          </div>
                          <div>
                            <Label
                              className="text-xs text-indigo-700"
                              title="Scheme Discount Amount - per unit scheme discount ( = (Unit - Disc Amt) x Sch % )"
                            >
                              Sch Disc Amt
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.scheme_discount_amount ?? 0}
                              onChange={(e) => updateDiscountField(index, 'scheme_discount_amount', e.target.value)}
                              className="h-9"
                              data-testid={`grn-line-sch-amt-${index}`}
                            />
                          </div>
                          <div>
                            <Label
                              className="text-xs text-indigo-700"
                              title="Gross Scheme Discount Amount - line total scheme discount (= Qty x Sch Disc Amt)"
                            >
                              Gross Sch Disc Amt
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.gross_scheme_discount_amount ?? 0}
                              onChange={(e) => updateDiscountField(index, 'gross_scheme_discount_amount', e.target.value)}
                              className="h-9 bg-white font-medium"
                              data-testid={`grn-line-gross-sch-${index}`}
                            />
                          </div>
                        </div>
                        
                        {/* Batch Info Row */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3 bg-amber-50 p-3 rounded-lg">
                          <div>
                            <Label className="text-xs text-amber-700">Batch # *</Label>
                            <Input
                              value={item.lot_batch_number}
                              onChange={(e) => updateItem(index, 'lot_batch_number', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, index, 'batch')}
                              placeholder="LOT123"
                              data-testid={`grn-line-batch-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-amber-700">Mfg Date</Label>
                            <Input
                              type="date"
                              value={item.manufacturing_date}
                              onChange={(e) => updateItem(index, 'manufacturing_date', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, index, 'mfg')}
                              data-testid={`grn-line-mfg-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-amber-700">Best Before</Label>
                            <Input
                              type="date"
                              value={item.best_before_date}
                              onChange={(e) => updateItem(index, 'best_before_date', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, index, 'bestbefore')}
                              data-testid={`grn-line-bestbefore-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-amber-700">Expiry Date *</Label>
                            <Input
                              type="date"
                              value={item.expiry_date}
                              onChange={(e) => updateItem(index, 'expiry_date', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, index, 'expiry')}
                              min={new Date().toISOString().split('T')[0]}
                              data-testid={`grn-line-expiry-${index}`}
                            />
                            {getExpiryBadge(item.expiry_date)}
                          </div>
                          <div>
                            <Label className="text-xs text-amber-700">MRP *</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.mrp}
                              onChange={(e) => updateItemAndRecalcPricing(index, 'mrp', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, index, 'mrp')}
                              data-testid={`grn-line-mrp-${index}`}
                            />
                          </div>
                        </div>
                        
                        {/* Selling Prices - only levels configured in Product Master */}
                        <div className="bg-blue-50 p-3 rounded-lg space-y-2">
                          <Label className="text-xs font-semibold text-blue-700">
                            Selling Prices (from Product Master)
                          </Label>
                          {(item.sp_levels || []).length === 0 ? (
                            <p className="text-xs text-gray-500" data-testid={`grn-line-no-pricing-levels-${index}`}>
                              No pricing levels configured in Product Master for this item.
                            </p>
                          ) : (
                            <div className="grid grid-cols-5 gap-2">
                              {(item.sp_levels || []).map((n) => (
                                <div key={n} className="bg-white p-2 rounded border space-y-1">
                                  <Label className="text-xs text-blue-700 font-medium">{SP_LABELS[n]}</Label>
                                  <div className="flex gap-1">
                                    <Select
                                      value={item[`pricing_type_${n}`] || 'markup'}
                                      onValueChange={(val) => updateSPPricing(index, n, val, item[`pricing_percent_${n}`] || 0)}
                                    >
                                      <SelectTrigger className="w-16 h-7 text-xs" data-testid={`grn-line-sp${n}-type-${index}`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="markup">+%</SelectItem>
                                        <SelectItem value="markdown">-%</SelectItem>
                                        <SelectItem value="flat">Fix</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    {item[`pricing_type_${n}`] !== 'flat' && (
                                      <Input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        value={item[`pricing_percent_${n}`] ?? ''}
                                        onChange={(e) => updateSPPricing(index, n, item[`pricing_type_${n}`], parseFloat(e.target.value) || 0)}
                                        className="w-14 h-7 text-xs"
                                        data-testid={`grn-line-sp${n}-percent-${index}`}
                                        placeholder="%"
                                      />
                                    )}
                                  </div>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item[`new_sp${n}`] ?? ''}
                                    onChange={(e) => updateItem(index, `new_sp${n}`, e.target.value ? parseFloat(e.target.value) : null)}
                                    onKeyDown={(e) => handleKeyDown(e, index, `sp${n}`)}
                                    className="h-8 text-sm"
                                    data-testid={`grn-line-sp${n}-${index}`}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Line Total */}
                        <div className="mt-3 text-right">
                          <span className="text-gray-500">Line Total: </span>
                          <span className="font-bold text-teal-600">₹{lineTotal.toFixed(2)}</span>
                        </div>
                      </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="bg-white">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-500" />
                Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes..."
                rows={3}
                data-testid="grn-notes"
              />
            </CardContent>
          </Card>
        </div>

        {/* Bill Adjustments + Grand Summary - full width horizontal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Vendor Snapshot */}
          {vendor && (
            <Card className="bg-white border-teal-200 lg:col-span-1">
              <CardHeader className="pb-2 bg-teal-50">
                <CardTitle className="text-sm text-teal-700">Vendor Details</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 text-sm">
                <p className="font-semibold">{vendor.vendor_name || vendor.name}</p>
                {vendor.gstin && <p className="text-gray-500">GSTIN: {vendor.gstin}</p>}
              </CardContent>
            </Card>
          )}

          {/* Bill Adjustments */}
          <Card className={`bg-white ${vendor ? 'lg:col-span-1' : 'lg:col-span-2'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Bill Adjustments</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Bill Disc %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={billDiscountPercent}
                  onChange={(e) => setBillDiscountPercent(parseFloat(e.target.value) || 0)}
                  className="text-right"
                  data-testid="grn-bill-discount-percent"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Other (+)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={otherChargesAdd}
                  onChange={(e) => setOtherChargesAdd(parseFloat(e.target.value) || 0)}
                  className="text-right"
                  data-testid="grn-other-charges-add"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Other (-)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={otherChargesDeduct}
                  onChange={(e) => setOtherChargesDeduct(parseFloat(e.target.value) || 0)}
                  className="text-right"
                  data-testid="grn-other-charges-deduct"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Round Off</Label>
                <Input
                  type="text"
                  value={roundOff}
                  onChange={handleRoundOffChange}
                  className="text-right"
                  placeholder="Can be negative"
                  data-testid="grn-round-off"
                />
              </div>
            </CardContent>
          </Card>

          {/* Grand Summary */}
          <Card className="bg-gradient-to-br from-teal-50 to-white border-teal-200 lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-teal-800">Grand Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium">₹{totals.subtotal}</span>
              </div>
              <div className="flex justify-between text-red-600">
                <span>(-) Bill Discount</span>
                <span>-₹{totals.billDiscount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">GST Total</span>
                <span className="font-medium">₹{totals.gstTotal}</span>
              </div>
              <div className="border-t pt-2 flex justify-between text-xl font-bold">
                <span>Grand Total</span>
                <span className="text-teal-600" data-testid="grn-grand-total">₹{Math.round(Number(totals.grandTotal || 0)).toLocaleString('en-IN')}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>

      {/* Vendor Selection Dialog */}
      <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Vendor</DialogTitle>
          </DialogHeader>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search vendors..."
              value={vendorSearch}
              onChange={(e) => setVendorSearch(e.target.value)}
              className="pl-10"
              data-testid="grn-vendor-search"
            />
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {vendors
              .filter(v => !v.is_international) // Only local vendors for local GRN
              .filter(v => 
                !vendorSearch || 
                v.name?.toLowerCase().includes(vendorSearch.toLowerCase()) ||
                v.code?.toLowerCase().includes(vendorSearch.toLowerCase())
              )
              .map(v => (
                <div
                  key={v.id}
                  className="p-3 border rounded-lg cursor-pointer hover:border-teal-500 hover:bg-teal-50"
                  onClick={() => {
                    setVendor({
                      id: v.id,
                      vendor_id: v.id,
                      vendor_code: v.code,
                      vendor_name: v.name,
                      gstin: v.gstin
                    });
                    setVendorDialogOpen(false);
                  }}
                  data-testid={`grn-vendor-option-${v.id}`}
                >
                  <p className="font-medium">{v.name}</p>
                  <p className="text-sm text-gray-500">{v.code}</p>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Margin reduction - security code required */}
      <Dialog open={Boolean(marginBlock)} onOpenChange={(open) => !open && setMarginBlock(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="margin-security-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Critical: margin reduction
            </DialogTitle>
            <DialogDescription>
              This GRN reduces the margin on the products below. It requires management attention -
              enter the active Super Admin security code to proceed. Every change is logged.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3">
            {(marginBlock?.violations || []).map(v => (
              <div key={v.product_id} className="text-sm" data-testid={`margin-violation-${v.product_id}`}>
                <span className="font-medium text-gray-800">{v.product_name}</span>
                <span className="text-gray-500"> ({v.sku})</span>
                <span className="block text-red-700">
                  {v.old_margin_percent}% → {v.new_margin_percent}% (down {v.drop} points)
                </span>
              </div>
            ))}
          </div>

          <div>
            <Label className="text-gray-700">Super Admin security code</Label>
            <Input
              value={securityCode}
              onChange={(e) => setSecurityCode(e.target.value.toUpperCase())}
              placeholder="6-character code"
              maxLength={6}
              className="font-mono tracking-[0.3em] text-center text-lg"
              data-testid="margin-security-code-input"
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-1">
              HQ Settings → Security Code (Super Admin only). Codes expire 24 hours after they are generated.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMarginBlock(null)} data-testid="margin-security-cancel">
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              disabled={securityCode.length < 6 || saving}
              onClick={() => saveGRN(marginBlock?.complete, securityCode)}
              data-testid="margin-security-submit"
            >
              Authorise & Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Selection Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Product</DialogTitle>
          </DialogHeader>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, SKU, HSN..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              className="pl-10"
              autoFocus
              data-testid="grn-product-search"
            />
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {products.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                {productSearch.length < 2 ? 'Type at least 2 characters' : 'No products found'}
              </p>
            ) : (
              products.map(p => (
                <div
                  key={p.id}
                  className="p-3 border rounded-lg cursor-pointer hover:border-teal-500 hover:bg-teal-50"
                  onClick={() => addProduct(p)}
                  data-testid={`grn-product-option-${p.id}`}
                >
                  <p className="font-medium"><HighlightText text={p.name} query={productSearch} /></p>
                  <p className="text-sm text-gray-500"><HighlightText text={p.sku} query={productSearch} /></p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateGRNLocalPage;
